import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, Trash2, Eye, EyeOff, BookOpen, ArrowUp, ArrowDown, Sparkles } from 'lucide-react';
import { EduSubject, EduDomain, CreateSubjectRequest, UpdateSubjectRequest } from '../../../types/learning';
import { API_CONFIG } from '../../../config/api';
import { supabase } from '../../../lib/supabase';
import toast from 'react-hot-toast';

const SubjectManagement: React.FC = () => {
  const [subjects, setSubjects] = useState<EduSubject[]>([]);
  const [domains, setDomains] = useState<EduDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [domainFilter, setDomainFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingSubject, setEditingSubject] = useState<EduSubject | null>(null);
  const [generatingKnowledge, setGeneratingKnowledge] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateSubjectRequest>({
    domain_id: '',
    name: '',
    description: '',
    order_num: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  // 当搜索条件变化时重新获取数据
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchSubjects();
    }, 300); // 防抖300ms
    
    return () => clearTimeout(timeoutId);
  }, [searchTerm, domainFilter, statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([fetchSubjects(), fetchDomains()]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubjects = async () => {
    try {
      // 构建查询参数
      const params = new URLSearchParams({
        action: 'list',
        limit: '100'
      });
      
      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      
      if (domainFilter !== 'all') {
        params.append('domain_id', domainFilter);
      }
      
      if (statusFilter !== 'all') {
        params.append('status', statusFilter === 'active' ? '1' : '0');
      }
      
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/edu-subjects-admin-manage?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch subjects');

      const result = await response.json();
      if (result.success) {
        setSubjects(result.data.data || []);
      }
    } catch (error) {
      console.error('获取学科列表失败:', error);
      toast.error('获取学科列表失败');
    }
  };

  const fetchDomains = async () => {
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/edu-domains-admin-manage?action=list&limit=100`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch domains');

      const result = await response.json();
      if (result.success) {
        setDomains(result.data.data || []);
      }
    } catch (error) {
      console.error('获取领域列表失败:', error);
    }
  };

  const handleCreate = () => {
    setEditingSubject(null);
    setFormData({
      domain_id: '',
      name: '',
      description: '',
      order_num: subjects.length
    });
    setShowModal(true);
  };

  const handleEdit = (subject: EduSubject) => {
    setEditingSubject(subject);
    setFormData({
      domain_id: subject.domain_id,
      name: subject.name,
      description: subject.description || '',
      order_num: subject.order_num
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('请输入学科名称');
      return;
    }

    if (!formData.domain_id) {
      toast.error('请选择所属领域');
      return;
    }

    try {
      const url = editingSubject 
        ? `${API_CONFIG.FUNCTIONS_URL}/edu-subjects-admin-manage?action=update`
        : `${API_CONFIG.FUNCTIONS_URL}/edu-subjects-admin-manage?action=create`;
      
      const body = editingSubject 
        ? { ...formData, id: editingSubject.id } as UpdateSubjectRequest
        : formData;

      const response = await fetch(url, {
        method: editingSubject ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) throw new Error('Failed to save subject');

      const result = await response.json();
      if (result.success) {
        toast.success(editingSubject ? '学科更新成功' : '学科创建成功');
        setShowModal(false);
        await fetchSubjects();
      } else {
        throw new Error(result.error || 'Save failed');
      }
    } catch (error) {
      console.error('保存学科失败:', error);
      toast.error('保存失败：' + (error as Error).message);
    }
  };

  const handleDelete = async (subject: EduSubject) => {
    if (!confirm(`确定要删除学科「${subject.name}」吗？此操作不可撤销。`)) return;

    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/edu-subjects-admin-manage?action=delete&id=${subject.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to delete subject');

      const result = await response.json();
      if (result.success) {
        toast.success('学科删除成功');
        await fetchSubjects();
      } else {
        throw new Error(result.error || 'Delete failed');
      }
    } catch (error) {
      console.error('删除学科失败:', error);
      toast.error('删除失败：' + (error as Error).message);
    }
  };

  const handleToggleStatus = async (subject: EduSubject) => {
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/edu-subjects-admin-manage?action=update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: subject.id,
          status: subject.status === 1 ? 0 : 1
        })
      });

      if (!response.ok) throw new Error('Failed to toggle status');

      const result = await response.json();
      if (result.success) {
        toast.success(`学科已${subject.status === 1 ? '禁用' : '启用'}`);
        await fetchSubjects();
      }
    } catch (error) {
      console.error('切换状态失败:', error);
      toast.error('操作失败');
    }
  };

  const handleGenerateKnowledge = async (subject: EduSubject) => {
    if (!confirm(`确定要为学科「${subject.name}」自动生成知识点吗？此操作将调用AI大模型。`)) return;

    try {
      setGeneratingKnowledge(subject.id);
      toast.loading('正在生成知识点，请稍候...', { id: 'generating' });

      console.log('🔍 调试信息 - 传递给Edge Function的数据:', {
        subjectId: subject.id,
        subjectIdType: typeof subject.id,
        subjectName: subject.name,
        fullSubject: subject
      });

      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/aimodule-content-admin-generate-edu?type=knowledge_points`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subject_id: subject.id
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate knowledge points');
      }

      const result = await response.json();
      if (result.success) {
        toast.success(`成功为「${subject.name}」生成 ${result.data.generated_count || 0} 个知识点`, { id: 'generating' });
      } else {
        throw new Error(result.error || 'Generation failed');
      }
    } catch (error) {
      console.error('生成知识点失败:', error);
      toast.error('生成失败：' + (error as Error).message, { id: 'generating' });
    } finally {
      setGeneratingKnowledge(null);
    }
  };

  const getDomainName = (domainId: string) => {
    const domain = domains.find(d => d.id === domainId);
    return domain?.name || '未知领域';
  };

  // 直接使用从后端获取的已过滤数据
  const filteredSubjects = subjects;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <BookOpen className="w-6 h-6 mr-2 text-blue-600" />
              学科管理
            </h2>
            <button
              onClick={handleCreate}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <Plus className="w-4 h-4 mr-2" />
              添加学科
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {/* 搜索和筛选 */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="搜索学科名称或描述..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <select
              value={domainFilter}
              onChange={(e) => setDomainFilter(e.target.value)}
              className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">全部领域</option>
              {domains.map(domain => (
                <option key={domain.id} value={domain.id}>{domain.name}</option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">全部状态</option>
              <option value="active">已启用</option>
              <option value="inactive">已禁用</option>
            </select>
          </div>

          {/* 学科列表 */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    学科信息
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    所属领域
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    排序
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    状态
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    创建时间
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSubjects.map((subject) => (
                  <tr key={subject.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{subject.name}</div>
                        {subject.description && (
                          <div className="text-sm text-gray-500">{subject.description}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{getDomainName(subject.domain_id)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-900">{subject.order_num}</span>
                        <div className="flex flex-col">
                          <button className="text-gray-400 hover:text-gray-600">
                            <ArrowUp className="w-3 h-3" />
                          </button>
                          <button className="text-gray-400 hover:text-gray-600">
                            <ArrowDown className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleStatus(subject)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          subject.status === 1
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                        }`}
                      >
                        {subject.status === 1 ? (
                          <><Eye className="w-3 h-3 mr-1" />启用</>
                        ) : (
                          <><EyeOff className="w-3 h-3 mr-1" />禁用</>
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(subject.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleGenerateKnowledge(subject)}
                          disabled={generatingKnowledge === subject.id}
                          className={`${
                            generatingKnowledge === subject.id
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-purple-600 hover:text-purple-900'
                          }`}
                          title="生成知识点"
                        >
                          {generatingKnowledge === subject.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                          ) : (
                            <Sparkles className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleEdit(subject)}
                          className="text-blue-600 hover:text-blue-900"
                          title="编辑"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(subject)}
                          className="text-red-600 hover:text-red-900"
                          title="删除"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredSubjects.length === 0 && (
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">暂无学科数据</p>
            </div>
          )}
        </div>
      </div>

      {/* 创建/编辑模态框 */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">
              {editingSubject ? '编辑学科' : '创建学科'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  所属领域 <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.domain_id}
                  onChange={(e) => setFormData({ ...formData, domain_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">请选择领域</option>
                  {domains.filter(d => d.status === 1).map(domain => (
                    <option key={domain.id} value={domain.id}>{domain.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  学科名称 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="请输入学科名称"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  描述
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="请输入学科描述"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  排序序号
                </label>
                <input
                  type="number"
                  value={formData.order_num}
                  onChange={(e) => setFormData({ ...formData, order_num: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  min="0"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingSubject ? '更新' : '创建'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubjectManagement;